/**
 * FieldPropAddUpdate.tsx
 * Field-level property editor with tabbed navigation.
 * Redesigned — uses shared GenWizard light theme.
 *
 * LAZY-PATH FIX
 * ─────────────────────────────────────────────────────────────────────────
 * In the lazy architecture, supportingData is null (MyComponent no longer
 * pre-loads DDM_PROCESS_DATA). Profile data is instead attached directly
 * onto each field node as node.profile by getDMProfile/getDMProfilePage
 * (server-side join). The component now reads profile from:
 *   1. initialData.profile          ← lazy path  (primary)
 *   2. supportingData lookup        ← legacy path (fallback)
 *
 * All existing behaviour for the non-lazy path is 100% unchanged.
 * ─────────────────────────────────────────────────────────────────────────
 */

import React, { Component, ChangeEvent } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import { T, cls, injectTheme } from './theme/theme';
import { getDuplicateValues, removeDuplicates } from './constants';

import LookupDataGrid from './LookupDataGrid';
import SurvivorshipSystemOrder from './SurvivorshipSystemOrder';
import DataProfile from './DataProfile';
import DataProfilingChart from './ddm/DataProfilingChart';
import DataValidationResults from './ddm/DataValidationResults';
import DataQualityReviewPage from './ddm/DataQualityReviewPage';
import DQRulesImpactTab from './ddm/DataQualityRulesImpactTab';

// ─── Types ────────────────────────────────────────────────────────────────────
interface FieldPropAddUpdateProps {
  selectedApp: any;
  tool: string;
  dataModelData?: any;
  handleSave: (data: any) => void;
  initialData: any;
  srcSystems: any;
  supportingData?: Record<string, any> | null;   // optional — absent in lazy path
}

interface FieldPropAddUpdateState {
  fieldData: any;
  tabKey: string;
  insightOverride: any | null;
}

// ─── Tab definitions ──────────────────────────────────────────────────────────
const buildTabs = (tool: string) => [
  ...(['BUILD_DQ_USING_DATA'].indexOf(tool) < 0
    ? [{ key: 'home',             label: 'Properties',         icon: '🏷️' }] : []),
  { key: 'dataProfile',           label: 'Data Profile',        icon: '📊' },
  ...(['BUILD_DQ_USING_DATA', 'BUILD_MDM_USING_DATA'].indexOf(tool) > -1
    ? [
        { key: 'dataQualityReview', label: 'Review Suggestions', icon: '🔀' },
        { key: 'impact',            label: 'Rules Applied',       icon: '📈' },
      ] : []),
  ...(['MIGRATION_INFA_LEGACY_TO_RELTIO', 'MIGRATION_INFA_LEGACY_TO_IDMC', 'BUILD_MDM_USING_DATA'].indexOf(tool) > -1
    ? [{ key: 'survivorships',    label: 'Survivorships',       icon: '🔀' }] : []),
];

// ─── Component ────────────────────────────────────────────────────────────────
class FieldPropAddUpdate extends Component<FieldPropAddUpdateProps, FieldPropAddUpdateState> {
  constructor(props: FieldPropAddUpdateProps) {
    super(props);
    this.state = {
      fieldData: { ...props.initialData },
      tabKey: props.tool === 'BUILD_DQ_USING_DATA' ? 'dataProfile' : 'home',
      insightOverride: null,
    };
  }

  componentDidMount() {
    injectTheme();
    this.updateInsightOverride(this.state.fieldData);
  }

  componentDidUpdate(prevProps: FieldPropAddUpdateProps) {
    let updatedFieldData = this.state.fieldData;
    if (prevProps.initialData !== this.props.initialData) {
      updatedFieldData = { ...this.props.initialData };
      this.setState({ fieldData: updatedFieldData });
    }
    if (prevProps.supportingData !== this.props.supportingData ||
        prevProps.initialData   !== this.props.initialData) {
      this.updateInsightOverride(updatedFieldData);
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────────

  /**
   * Resolves the insight for the current field.
   *
   * Priority:
   *   1. fieldData.profile.insight  — lazy path: insight already on the node
   *   2. supportingData lookup      — legacy path: insight in process data
   */
  updateInsightOverride(fieldData: any) {
    // ── Lazy path: insight is directly on the field node ──
    if (fieldData?.profile?.insight) {
      this.setState({ insightOverride: fieldData.profile.insight });
      return;
    }

    // ── Legacy path: hunt through supportingData ──────────
    const { supportingData } = this.props;
    if (!supportingData || !fieldData) return;
    const results = supportingData['data_profiling_results']?.profiling_results || [];
    const col = this.getOrigColNm(fieldData);
    const found = results.find((f: any) => f.column_name === col);
    if (found?.insight) this.setState({ insightOverride: found.insight });
  }

  /**
   * Returns the original column name used to look up profiling data.
   *
   * Priority:
   *   1. field.profile.orig_column_name  — lazy path (most reliable)
   *   2. FLD_PHYSICAL_ID split           — legacy path
   *   3. field.profile.column_name       — lazy fallback
   *   4. COLUMN_NAME                     — last resort
   */
  getOrigColNm(fieldData: any): string {
    return (
      fieldData?.profile?.orig_column_name   ||
      fieldData?.FLD_PHYSICAL_ID?.split('-')?.[1] ||
      fieldData?.profile?.column_name        ||
      fieldData?.COLUMN_NAME                 ||
      ''
    );
  }

  // ── Event handlers ──────────────────────────────────────────────────────
  handleTabSelect = (key: string) => this.setState({ tabKey: key });

  handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    let { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (name === 'DATA_LEN_PRECISION') {
      value = value + '|' + (this.state.fieldData['DATA_LEN'] || '').split('|')[1];
      name = 'DATA_LEN';
    } else if (name === 'DATA_LEN_SCALE') {
      value = (this.state.fieldData['DATA_LEN'] || '').split('|')[0] + '|' + value;
      name = 'DATA_LEN';
    }
    this.setState(prev => ({
      fieldData: { ...prev.fieldData, [name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : value },
    }));
  };

  handleLookUpValueChanges = (data: any) => {
    const fData = { ...this.state.fieldData };
    fData.LOOKUP_RDS_CV = data.map((x: any) => ({ Code: x.Code, Name: x.Name }));
    this.setState({ fieldData: fData });
  };

  handleSaveSystemOrderChanges = (data: any) => {
    if (!data) return;
    const fData = { ...this.state.fieldData, SYSTEM_ORDER: data };
    this.setState({ fieldData: fData });
  };

  handleInsightUpdate   = (i: any) => this.setState({ insightOverride: i });

  handleShouldBeLookupUpdate = (sel: any) => {
    if (!sel) return;
    const fData = { ...this.state.fieldData };
    fData.DATA_TYP         = 'LookupField';
    fData.LOOKUP_RDS_NM    = sel.rdsName;
    fData.LOOKUP_RDS_DISP_NM = sel.rdsLabel;
    fData.LOOKUP_RDS_CV    = sel.lookupValues.map((l: any) => ({ id: l.i, Code: l.c, Name: l.d }));
    this.setState({ fieldData: fData });
  };

  handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const fData = this.state.fieldData;

    if (fData.DATA_TYP === 'LookupField') {
      if (!fData.LOOKUP_RDS_CV?.length) { alert('Missing lookup values'); return; }
      const dups = getDuplicateValues(fData.LOOKUP_RDS_CV);
      if (dups.duplicateCodes?.length) {
        confirmAlert({
          customUI: ({ onClose }) => (
            <div style={{
              padding: 24, borderRadius: 12, background: T.bgCard,
              border: `1px solid ${T.border}`, boxShadow: '0 8px 32px rgba(0,0,0,.12)',
              fontFamily: "'Sora', system-ui, sans-serif",
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: T.textPrimary }}>
                Duplicate Codes Found
              </div>
              <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 16 }}>
                {dups.duplicateCodes.join(', ')}
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="gw-btn gw-btn-ghost gw-btn-sm" onClick={onClose}>Cancel</button>
                <button className="gw-btn gw-btn-primary gw-btn-sm" onClick={() => {
                  fData.LOOKUP_RDS_CV = removeDuplicates(fData.LOOKUP_RDS_CV);
                  this.props.handleSave(fData);
                  onClose();
                }}>Remove &amp; Save</button>
              </div>
            </div>
          ),
        });
        return;
      }
    }
    this.props.handleSave(fData);
  };

  // ── Render helpers ──────────────────────────────────────────────────────
  renderHeader(mandatory: boolean) {
    const { fieldData } = this.state;
    const typeBadge =
      fieldData.DATA_TYP === 'LookupField' ? 'amber' :
      fieldData.DATA_TYP?.includes('Date') ? 'green' : 'brand';

    return (
      <div className="gw-field-hdr">
        <div>
          <div className="gw-field-hdr-name">
            {fieldData.COLUMN_DISPLAY_NAME || fieldData.COLUMN_NAME}
          </div>
          <div className="gw-field-hdr-meta">
            <span className={cls('gw-badge', `gw-badge-${typeBadge}`)}>
              {(fieldData.DATA_TYP || '').replace('Field', '')}
            </span>
            <span className={cls('gw-badge', mandatory ? 'gw-badge-green' : 'gw-badge-muted')}>
              {mandatory ? 'Required' : 'Optional'}
            </span>
          </div>
        </div>
        <button
          className="gw-btn gw-btn-primary gw-btn-sm"
          onClick={() => this.handleSubmit()}
        >
          Save
        </button>
      </div>
    );
  }

  renderPropertiesTab() {
    const { fieldData } = this.state;
    const mandatory = fieldData?.MANDATORY === 'Y';

    return (
      <div>
        <div className="gw-section-hdr">Basic Info</div>
        <div className="gw-form-grid">
          <div className="gw-form-group">
            <label className="gw-label">Name</label>
            <input className="gw-input" name="COLUMN_NAME"
              value={fieldData.COLUMN_NAME || ''} onChange={this.handleChange} />
          </div>
          <div className="gw-form-group">
            <label className="gw-label">Display Label</label>
            <input className="gw-input" name="COLUMN_DISPLAY_NAME"
              value={fieldData.COLUMN_DISPLAY_NAME || ''} onChange={this.handleChange} />
          </div>
        </div>

        <div className="gw-form-grid gw-form-grid-1">
          <div className="gw-form-group">
            <label className="gw-label">Description</label>
            <input className="gw-input" name="COLUMN_SHORT_DESC"
              value={fieldData.COLUMN_SHORT_DESC || ''} onChange={this.handleChange} />
          </div>
        </div>

        <div className="gw-form-grid gw-form-grid-1" style={{ marginBottom: 20 }}>
          <div className="gw-form-group">
            <label className="gw-label">Long Description</label>
            <textarea className="gw-textarea" name="COLUMN_LONG_DESC"
              value={fieldData.COLUMN_LONG_DESC || ''} onChange={this.handleChange} />
          </div>
        </div>

        <div className="gw-section-hdr">Type &amp; Constraints</div>
        <div className="gw-form-grid">
          <div className="gw-form-group">
            <label className="gw-label">Data Type</label>
            <select className="gw-select" name="DATA_TYP"
              value={fieldData.DATA_TYP || ''} onChange={this.handleChange}>
              {['TextField','IntegerField','DecimalField','DateField',
                'DateTimeField','LookupField','DoubleField','BooleanField'].map(o => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="gw-form-group">
            {fieldData.DATA_TYP === 'TextField' && <>
              <label className="gw-label">Max Length</label>
              <input className="gw-input" type="number" name="DATA_LEN"
                value={fieldData.DATA_LEN || ''} onChange={this.handleChange} />
            </>}
            {fieldData.DATA_TYP === 'DecimalField' && <>
              <label className="gw-label">Precision</label>
              <input className="gw-input" type="number" name="DATA_LEN_PRECISION"
                value={(fieldData.DATA_LEN || '').split('|')[0]} onChange={this.handleChange}
                style={{ marginBottom: 8 }} />
              <label className="gw-label">Scale</label>
              <input className="gw-input" type="number" name="DATA_LEN_SCALE"
                value={(fieldData.DATA_LEN || '').split('|')[1]} onChange={this.handleChange} />
            </>}
          </div>
        </div>

        <div className="gw-checkbox-row" style={{ marginBottom: 20 }}>
          <input id="mandatory-cb" type="checkbox" className="gw-checkbox"
            name="MANDATORY" checked={mandatory} onChange={this.handleChange} />
          <label htmlFor="mandatory-cb" className="gw-checkbox-label">Required field</label>
        </div>

        {fieldData.DATA_TYP === 'LookupField' && <>
          <div className="gw-section-hdr">Lookup Configuration</div>
          <div className="gw-form-grid" style={{ marginBottom: 16 }}>
            <div className="gw-form-group">
              <label className="gw-label">Dataset Name</label>
              <input className="gw-input" name="LOOKUP_RDS_NM"
                value={fieldData.LOOKUP_RDS_NM || ''} onChange={this.handleChange} />
            </div>
            <div className="gw-form-group">
              <label className="gw-label">Dataset Description</label>
              <input className="gw-input" name="LOOKUP_RDS_DISP_NM"
                value={fieldData.LOOKUP_RDS_DISP_NM || ''} onChange={this.handleChange} />
            </div>
          </div>
          <LookupDataGrid
            data={fieldData.LOOKUP_RDS_CV}
            handleLookUpValueChanges={this.handleLookUpValueChanges}
          />
        </>}
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────
  render() {
    const { fieldData, tabKey, insightOverride } = this.state;
    const mandatory = fieldData?.MANDATORY === 'Y';

    // ── Resolve profiling data ───────────────────────────────────────────
    // LAZY PATH:  initialData.profile is attached server-side by getDMProfile.
    //             Use it directly — no supportingData lookup needed.
    // LEGACY PATH: hunt through supportingData (original behaviour).

    let ddmFldProfilingData: any = null;
    let ddmRecordStore: any[]    = [];
    let ddmFieldDVData: any      = null;
    let ddmDVRecordStore: any[]  = [];
    let lkpPairData: any         = null;

    // ── Lazy path: profile already on the node ────────────────────────────
    if (fieldData?.profile) {
      ddmFldProfilingData = {
        ...fieldData.profile,
        // merge insightOverride so rule-lifecycle edits are reflected live
        insight: insightOverride ?? fieldData.profile.insight,
      };
      // Validation results are nested inside profile.insight in the lazy shape
      const vr = fieldData.profile?.insight?.validation_results;
      if (Array.isArray(vr) && vr.length) {
        const origCol = this.getOrigColNm(fieldData);
        ddmFieldDVData = vr.find((f: any) => f.column === origCol) ?? { validation_results: vr };
      }
    }

    // ── Legacy path: supportingData lookup (unchanged) ────────────────────
    if (!ddmFldProfilingData && this.props.supportingData) {
      const sd      = this.props.supportingData;
      const origCol = this.getOrigColNm(fieldData);
      const results = sd['data_profiling_results']?.profiling_results || [];

      ddmFldProfilingData  = results.find((f: any) => f.column_name === origCol) || null;
      ddmRecordStore       = sd['data_profiling_results']?.record_store || [];
      const dvResults      = sd['vr']?.validation_results || [];
      ddmFieldDVData       = dvResults.find((f: any) => f.column === origCol) || null;
      ddmDVRecordStore     = sd['vr']?.record_store || [];
      lkpPairData          = sd['lkp_pairs'];

      if (ddmFldProfilingData) {
        ddmFldProfilingData = {
          ...ddmFldProfilingData,
          insight: insightOverride || ddmFldProfilingData.insight,
        };
      }
    }

    const tabs = buildTabs(this.props.tool);

    return (
      <div className="gw-root" style={{ fontFamily: "'Sora', system-ui, sans-serif" }}>

        {this.renderHeader(mandatory)}

        {/* Tab bar */}
        <div className="gw-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={cls('gw-tab', tabKey === tab.key && 'active')}
              onClick={() => this.handleTabSelect(tab.key)}
            >
              <span className="gw-tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ padding: '20px 18px' }}>

          {tabKey === 'home' && this.renderPropertiesTab()}

          {tabKey === 'dataProfile' && (
            ddmFldProfilingData
              ? <DataProfilingChart data={ddmFldProfilingData} recordStore={ddmRecordStore} />
              : <DataProfile columnData={fieldData.COLUMN_DATA} tableData={fieldData.TABLE_DATA} />
          )}

          {tabKey === 'dataQuality' && (
            ddmFieldDVData
              ? <DataValidationResults data={ddmFieldDVData} recordStore={ddmDVRecordStore} />
              : <div className="gw-empty"><div className="gw-empty-label">No validation data available.</div></div>
          )}

          {tabKey === 'dataQualityReview' && (
            ddmFldProfilingData
              ? <DataQualityReviewPage
                  selectedApp={this.props.selectedApp}
                  dataModelData={this.props.dataModelData}
                  profilingData={ddmFldProfilingData}
                  lkpPairData={lkpPairData}
                  onInsightUpdate={this.handleInsightUpdate}
                  onShouldBeLookupUpdate={this.handleShouldBeLookupUpdate}
                />
              : <div className="gw-empty"><div className="gw-empty-label">No profiling data available.</div></div>
          )}

          {tabKey === 'impact' && (
            <DQRulesImpactTab
              insight={insightOverride ?? fieldData?.profile?.insight}
            />
          )}

          {tabKey === 'survivorships' && (
            <SurvivorshipSystemOrder
              systemOrder={fieldData.SYSTEM_ORDER}
              srcSystems={this.props.srcSystems}
              handleSaveSystemOrderChanges={this.handleSaveSystemOrderChanges}
            />
          )}
        </div>
      </div>
    );
  }
}

export default FieldPropAddUpdate;